import { useRef, isValidElement, cloneElement, useEffect, useCallback, createContext, useContext } from 'react';
import { mergeRefs } from '../../utils/mergeRefs.js';
import { jsx } from '@emotion/react';

function createCollection(name) {
  const COLLECTION_DATA_ATTRIBUTE = "data-collection-item";
  const CollectionContext = /*#__PURE__*/createContext({
    collectionRef: {
      current: null
    },
    itemMap: new Map()
  });

  /**
   * CollectionProvider
   *
   * Provides context to the underlying `CollectionContainer` and
   * `CollectionItem` components.
   */
  const CollectionProvider = props => {
    const collectionRef = useRef(null);
    const itemMap = useRef(new Map()).current;
    const context = {
      collectionRef,
      itemMap
    };
    return jsx(CollectionContext.Provider, {
      value: context
    }, props.children);
  };
  CollectionProvider.displayName = `${name}CollectionProvider`;
  const useCollectionContext = () => useContext(CollectionContext);

  /**
   * CollectionContainer
   *
   * Attaches a ref that's used to query the DOM to determine the order of
   * `CollectionItem` elements.
   */
  const CollectionContainer = props => {
    const {
      children
    } = props;
    const {
      collectionRef
    } = useCollectionContext();
    if (! /*#__PURE__*/isValidElement(children)) {
      return null;
    }
    return /*#__PURE__*/cloneElement(children, {
      // @ts-ignore
      ref: mergeRefs(collectionRef, children == null ? void 0 : children.ref)
    });
  };
  CollectionContainer.displayName = `${name}CollectionContainer`;

  /**
   * CollectionItem
   *
   * Handles the "registration" and "deregistration" of items as they're
   * mounted and unmounted. Attaches a ref and data attribute to its
   * `children` for easy access.
   */
  const CollectionItem = props => {
    const {
      children,
      ...itemData
    } = props;
    const itemRef = useRef(null);
    const {
      itemMap
    } = useCollectionContext();
    useEffect(() => {
      // Register item
      itemMap.set(itemRef, {
        ref: itemRef,
        ...itemData
      });

      // Unregister item when unmounted
      // eslint-disable-next-line
      return () => void itemMap.delete(itemRef);
    });
    if (! /*#__PURE__*/isValidElement(children)) {
      return null;
    }
    return /*#__PURE__*/cloneElement(children, {
      // @ts-ignore
      ref: mergeRefs(itemRef, children == null ? void 0 : children.ref),
      [COLLECTION_DATA_ATTRIBUTE]: ""
    });
  };
  CollectionItem.displayName = `${name}CollectionItem`;

  /**
   * useCollection
   *
   * A hook to query for the current list of items in a collection.
   */
  /* eslint-disable @typescript-eslint/explicit-function-return-type */
  const useCollection = () => {
    const {
      collectionRef,
      itemMap
    } = useCollectionContext();
    const getItems = useCallback(() => {
      const collectionNode = collectionRef.current;
      if (!collectionNode) {
        return [];
      }

      /**
       * Use the DOM as the source-of-truth for node order since nodes can be
       * added and removed from the `itemMap` dynamically.
       */
      const orderedNodes = Array.from(collectionNode.querySelectorAll(`[${COLLECTION_DATA_ATTRIBUTE}]`));
      // Grab all refs that currently exist from the `itemMap`.
      const items = Array.from(itemMap.values());
      const orderedItems = items.sort((a, b) => orderedNodes.indexOf(a.ref.current) - orderedNodes.indexOf(b.ref.current));
      return orderedItems;
    }, [collectionRef, itemMap]);
    return {
      getItems
    };
  };
  return {
    CollectionProvider,
    CollectionContainer,
    CollectionItem,
    useCollection
  };
}

export { createCollection };
